import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  Box,
  Check,
  CheckCircle,
  CheckSquare,
  Clock,
  Copy,
  DollarSign,
  ExternalLink,
  Eye,
  MessageCircle,
  Package,
  Pencil,
  Radio,
  RefreshCw,
  Slash,
  Tag,
  X,
} from "lucide-react";

import { EmptyState } from "@/components/EmptyState";
import { Modal } from "@/components/Modal";
import { useCardsByIds } from "@/features/useCardsByIds";
import {
  calcNetProfit,
  generateId,
  supplyCostFor,
  useApp,
  type LiveListing,
  type Sale,
  type SaleSupplyItem,
  type SaleVenue,
} from "@/context/AppContext";
import { apiFetch } from "@/lib/apiClient";
import {
  endEbayListing,
  extendEbayListingDuration,
  getActiveEbayListings,
  getEbayListingMessages,
  relistEbayListing,
  type EndReason,
} from "@/features/ebayListings";
import { EditPriceModal } from "@/features/liveListings/EditPriceModal";
import { EditDetailsModal } from "@/features/liveListings/EditDetailsModal";
import { SendOfferModal } from "@/features/liveListings/SendOfferModal";
import { MessagesModal } from "@/features/liveListings/MessagesModal";
import {
  BulkEndModal,
  BulkExtendModal,
  BulkProgressModal,
  BulkRelistModal,
  type BulkProgressResult,
} from "@/features/liveListings/BulkModals";

type FilterValue = "all" | "active" | "ending-soon" | "ended";

const FILTERS: { value: FilterValue; label: string }[] = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "ending-soon", label: "Ending Soon" },
  { value: "ended", label: "Ended" },
];

const ENDING_SOON_MS = 86_400_000;
const RED_THRESHOLD_MS = 12 * 60 * 60 * 1000;
const YELLOW_THRESHOLD_MS = 3 * 86_400_000;

interface EndReasonOption {
  label: string;
  value: EndReason;
  description: string;
}

const END_REASONS: EndReasonOption[] = [
  {
    label: "Not available",
    value: "NotAvailable",
    description: "Item is no longer available",
  },
  {
    label: "Lost or broken",
    value: "LostOrBroken",
    description: "Item was lost or damaged",
  },
  {
    label: "Incorrect details",
    value: "Incorrect",
    description: "Listing has wrong info; will relist",
  },
  {
    label: "Sold elsewhere",
    value: "OtherListingError",
    description: "Sold on another channel",
  },
];

function calcAutoFees(
  salePrice: number,
  venue: SaleVenue,
): { platformFees: number; paymentFees: number } {
  if (venue === "eBay") {
    return {
      platformFees: salePrice * 0.1325,
      paymentFees: salePrice * 0.029 + 0.3,
    };
  }
  if (venue === "TCGplayer") {
    return {
      platformFees: salePrice * 0.1099,
      paymentFees: salePrice * 0.029 + 0.3,
    };
  }
  if (venue === "Facebook Marketplace") {
    return { platformFees: salePrice * 0.05, paymentFees: 0 };
  }
  return { platformFees: 0, paymentFees: 0 };
}

export const Route = createFileRoute("/live")({
  component: LiveScreen,
});

function LiveScreen() {
  const {
    liveListings,
    products,
    shippingSupplies,
    completeSale,
    deleteLiveListing,
    endLiveListing,
    updateLiveListing,
    updateListingSupplies,
    updateProduct,
    addLiveListing,
    relist,
  } = useApp();
  const navigate = useNavigate();

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const [filter, setFilter] = useState<FilterValue>("all");
  const [search, setSearch] = useState("");

  const [banner, setBanner] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => setBanner(null), 4000);
    return () => clearTimeout(t);
  }, [banner]);

  // Reconcile from eBay: eBay is authoritative for active listings (each created
  // with SKU = product id). Rebuilds any live listing that exists on eBay but is
  // missing locally, and self-heals the eBay link fields (id/URL/end time) on
  // listings that already exist — recovers lost records and corrects stale URLs.
  const [reconciling, setReconciling] = useState(false);
  const handleReconcile = useCallback(async () => {
    setReconciling(true);
    try {
      const active = await getActiveEbayListings();
      let recovered = 0;
      let repaired = 0;
      for (const item of active) {
        // Match each eBay item back to its product by SKU. Single-card listings
        // (SKU = card id) won't match a product and are skipped.
        const product = products.find((p) => p.id === item.sku);
        if (!product) continue;

        const existing = liveListings.find(
          (l) => l.ebayListingId === item.itemId || l.productId === product.id,
        );
        if (existing) {
          // Already represented — refresh the eBay-authoritative link fields so
          // a re-run corrects a stale id/URL/end time (leaves price/title alone).
          if (
            existing.ebayListingId !== item.itemId ||
            existing.ebayListingUrl !== item.viewItemUrl ||
            (item.endTime && existing.endsAt !== item.endTime)
          ) {
            updateLiveListing({
              ...existing,
              ebayListingId: item.itemId,
              ebayListingUrl: item.viewItemUrl,
              endsAt: item.endTime ?? existing.endsAt,
            });
            repaired += 1;
          }
          continue;
        }

        addLiveListing({
          id: generateId("LIVE"),
          productId: product.id,
          title: item.title || product.listingTitle || product.name,
          venue: "eBay",
          askingPrice: item.price ?? product.targetSalePrice ?? 0,
          cardCount: product.cardCount,
          startedAt: item.startTime ?? new Date().toISOString(),
          endsAt: item.endTime,
          ebayListingId: item.itemId,
          ebayListingUrl: item.viewItemUrl,
          listingType: item.listingType,
          startingBid: item.listingType === "Auction" ? item.price : undefined,
          reservePrice: item.reservePrice,
          buyItNowPrice: item.buyItNowPrice,
        });
        if (product.status !== "Listed") {
          updateProduct({ ...product, status: "Listed" });
        }
        recovered += 1;
      }
      const parts: string[] = [];
      if (recovered) parts.push(`${recovered} recovered`);
      if (repaired) parts.push(`${repaired} updated`);
      setBanner({
        type: "success",
        text: parts.length
          ? `Reconciled from eBay — ${parts.join(", ")}.`
          : "No changes — already in sync with eBay.",
      });
    } catch (err) {
      setBanner({
        type: "error",
        text:
          err instanceof Error ? err.message : "Reconcile from eBay failed.",
      });
    } finally {
      setReconciling(false);
    }
  }, [
    liveListings,
    products,
    addLiveListing,
    updateLiveListing,
    updateProduct,
  ]);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [soldConfirm, setSoldConfirm] = useState<{
    listingId: string;
    salePrice: number;
  } | null>(null);
  const [endListingId, setEndListingId] = useState<string | null>(null);
  const [endReason, setEndReason] = useState<EndReason | null>(null);
  const [endSubmitting, setEndSubmitting] = useState(false);
  const [editSuppliesListingId, setEditSuppliesListingId] = useState<
    string | null
  >(null);
  const [draftSupplyIds, setDraftSupplyIds] = useState<string[]>([]);
  const [editPriceListing, setEditPriceListing] = useState<LiveListing | null>(
    null,
  );
  const [editDetailsListing, setEditDetailsListing] =
    useState<LiveListing | null>(null);
  const [sendOfferListing, setSendOfferListing] = useState<LiveListing | null>(
    null,
  );
  const [messagesListing, setMessagesListing] = useState<LiveListing | null>(
    null,
  );
  const [unreadMessageCounts, setUnreadMessageCounts] = useState<
    Record<string, number>
  >({});

  // ── Bulk select mode ────────────────────────────────────────────────────────
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkConfirm, setBulkConfirm] = useState<
    "end" | "extend" | "relist" | null
  >(null);
  const [bulkEndReason, setBulkEndReason] = useState<EndReason | null>(null);
  const [bulkDuration, setBulkDuration] = useState<number | "GTC">(7);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{
    action: "end" | "extend" | "relist";
    total: number;
    done: number;
    results: BulkProgressResult[];
  } | null>(null);

  const enterSelectMode = () => {
    setSelectMode(true);
    setSelectedIds(new Set());
  };
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
    setBulkConfirm(null);
    setBulkEndReason(null);
  };
  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  // Cross-listing supersede outcome banner (TNK-102). Cleared automatically a
  // few seconds after Mark Sold; surfaces "ended N other listings (eBay: ok,
  // Facebook: failed)" so the operator knows what auto-closed.
  const [supersedeToast, setSupersedeToast] = useState<string | null>(null);
  const [watcherCounts, setWatcherCounts] = useState<Record<string, number>>(
    {},
  );
  const [listingTypeCache, setListingTypeCache] = useState<
    Record<string, "FixedPrice" | "Auction">
  >({});

  // Fetch watcher counts + cached listing type for active eBay BIN listings.
  // Needed so Send Offer button only appears when there are watchers to offer to.
  // Best-effort: failures stay silent and just leave the badge / button absent.
  const liveIdsKey = liveListings
    .filter((l) => l.venue === "eBay" && !!l.ebayListingId)
    .map((l) => l.id)
    .join(",");
  useEffect(() => {
    let cancelled = false;
    const candidates = liveListings.filter(
      (l) =>
        l.venue === "eBay" &&
        !!l.ebayListingId &&
        (l.listingType === "FixedPrice" || l.listingType === undefined) &&
        !(l.endsAt && new Date(l.endsAt).getTime() <= Date.now()),
    );
    void (async () => {
      for (const listing of candidates) {
        if (cancelled) return;
        if (watcherCounts[listing.id] !== undefined) continue;
        try {
          const res = await apiFetch(
            `/api/ebay/listings/${encodeURIComponent(listing.ebayListingId!)}`,
          );
          if (!res.ok) continue;
          const json = (await res.json()) as {
            listingType: "FixedPrice" | "Auction";
            watchCount?: number;
          };
          if (cancelled) return;
          setListingTypeCache((prev) => ({
            ...prev,
            [listing.id]: json.listingType,
          }));
          if (json.listingType === "FixedPrice") {
            setWatcherCounts((prev) => ({
              ...prev,
              [listing.id]: json.watchCount ?? 0,
            }));
          }
        } catch {
          /* silent */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveIdsKey]);

  // Fetch unread buyer-message counts for each eBay listing. Best-effort —
  // failures are silent so the badge just doesn't appear.
  useEffect(() => {
    let cancelled = false;
    const candidates = liveListings.filter(
      (l) => l.venue === "eBay" && !!l.ebayListingId,
    );
    void (async () => {
      for (const listing of candidates) {
        if (cancelled) return;
        if (unreadMessageCounts[listing.id] !== undefined) continue;
        try {
          const data = await getEbayListingMessages(listing.ebayListingId!);
          if (cancelled) return;
          setUnreadMessageCounts((prev) => ({
            ...prev,
            [listing.id]: data.unreadCount,
          }));
        } catch {
          /* silent */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveIdsKey]);

  // TNK-155 phase C: server-fetch every cardId referenced across all
  // packaged listings in one round-trip, then look each listing's cards
  // up via the resulting Map. Replaces the O(M*N) `cards.filter(...)`
  // inside the listings loop with O(M + K) where K is unique cardId
  // count.
  const allPackagedCardIds = useMemo(() => {
    const set = new Set<string>();
    for (const l of liveListings) {
      const product = products.find((p) => p.id === l.productId);
      for (const cid of product?.cardIds ?? []) set.add(cid);
    }
    return Array.from(set);
  }, [liveListings, products]);
  const { byId: packagedById } = useCardsByIds(allPackagedCardIds);

  const enrichedListings = useMemo(() => {
    return liveListings.map((l) => {
      const product = products.find((p) => p.id === l.productId);
      const packagedCards = (product?.cardIds ?? [])
        .map((cid) => packagedById.get(cid))
        .filter((c): c is NonNullable<typeof c> => !!c);
      const uniqueSupplyIds = [...new Set(l.supplyIds ?? [])];
      const supplies = uniqueSupplyIds
        .map((sid) => shippingSupplies.find((s) => s.id === sid))
        .filter((s): s is NonNullable<typeof s> => s !== undefined);
      const suppliesCost = supplies.reduce(
        (sum, s) => sum + supplyCostFor(s, packagedCards.length),
        0,
      );
      const cardValue = packagedCards.reduce(
        (s, c) => s + (c.marketPrice || c.expectedSalePrice || 0),
        0,
      );
      return {
        listing: l,
        product,
        packagedCards,
        supplies,
        suppliesCost,
        cardValue,
      };
    });
  }, [liveListings, products, packagedById, shippingSupplies]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enrichedListings.filter(({ listing }) => {
      if (q && !listing.title.toLowerCase().includes(q)) return false;
      // TNK-102: status is now the authoritative "is it live?" signal — a
      // listing past its auction endsAt is still status='Active' until
      // someone explicitly closes it. Date-based "ending soon" still uses
      // endsAt for sorting hints.
      const status = listing.status ?? "Active";
      const isActiveStatus = status === "Active";
      const endsAtMs = listing.endsAt
        ? new Date(listing.endsAt).getTime()
        : null;
      const isEndingSoon =
        isActiveStatus &&
        endsAtMs !== null &&
        endsAtMs > now &&
        endsAtMs - now <= ENDING_SOON_MS;
      switch (filter) {
        case "active":
          return isActiveStatus;
        case "ending-soon":
          return isEndingSoon;
        case "ended":
          return !isActiveStatus;
        default:
          return true;
      }
    });
  }, [enrichedListings, filter, now, search]);

  const submitEndListing = async () => {
    if (!endListingId || !endReason) return;
    const listing = liveListings.find((l) => l.id === endListingId);
    if (!listing?.ebayListingId) return;
    setEndSubmitting(true);
    try {
      await endEbayListing(listing.ebayListingId, endReason);
      // Caller already pushed to eBay; ask the server to flip status='Ended'
      // without a second platform call.
      void endLiveListing(listing.id, {
        reason: endReason,
        pushExternal: false,
      });
      // endLiveListing defaults the product back to "Ready to List", which is
      // only correct for the Incorrect reason (seller intends to relist).
      // For the other reasons the product is gone from inventory, so flip
      // the status to "Sold" instead.
      if (endReason !== "Incorrect") {
        const product = products.find((p) => p.id === listing.productId);
        if (product) {
          updateProduct({ ...product, status: "Sold" });
        }
      }
      setEndListingId(null);
      setEndReason(null);
      setBanner({ type: "success", text: "Listing ended on eBay" });
    } catch (err) {
      setBanner({
        type: "error",
        text: err instanceof Error ? err.message : "Failed to end listing",
      });
    } finally {
      setEndSubmitting(false);
    }
  };

  const handleCompleteSale = useCallback(async () => {
    if (!soldConfirm) return;
    const listing = liveListings.find((l) => l.id === soldConfirm.listingId);
    if (!listing) return;
    const product = products.find((p) => p.id === listing.productId);
    const { platformFees, paymentFees } = calcAutoFees(
      soldConfirm.salePrice,
      listing.venue,
    );
    const uniqueSupplyIds = [...new Set(listing.supplyIds ?? [])];
    const listingCardCount =
      listing.cardCount ?? (product?.cardIds ? product.cardIds.length : 0);
    const suppliesBreakdown: SaleSupplyItem[] = uniqueSupplyIds
      .map((sid) => shippingSupplies.find((s) => s.id === sid))
      .filter((s): s is NonNullable<typeof s> => s !== undefined)
      // Snapshot the resolved per-listing cost (× unitsPerUse, ×cardCount for
      // per-card supplies) so the sale record reflects what was actually
      // consumed, even if the supply's settings change later.
      .map((s) => ({
        id: s.id,
        name: s.name,
        unitCost: supplyCostFor(s, listingCardCount),
      }));

    // For eBay sales, try to fetch buyer info + order id from the
    // Fulfillment API so the operator doesn't have to type them. Failures
    // are non-fatal — the sale still saves, with empty buyer fields, and
    // the operator can edit on /sales/$id.
    let ebayOrderId: string | undefined;
    let ebayTransactionId: string | undefined;
    let buyerName: string | undefined;
    let buyerAddress: string | undefined;
    if (listing.venue === "eBay" && listing.ebayListingId) {
      try {
        const res = await apiFetch(
          `/api/ebay/orders/by-listing/${encodeURIComponent(listing.ebayListingId)}`,
        );
        if (res.ok) {
          const json = (await res.json()) as {
            orderId?: string;
            transactionId?: string;
            buyerName?: string;
            buyerAddress?: string;
          };
          ebayOrderId = json.orderId;
          ebayTransactionId = json.transactionId;
          buyerName = json.buyerName;
          buyerAddress = json.buyerAddress;
        }
      } catch {
        // Swallow — non-fatal.
      }
    }

    const sale: Sale = {
      id: generateId("SALE"),
      saleDate: new Date().toISOString().split("T")[0] ?? "",
      itemType: "Product",
      itemId: listing.productId,
      itemName: listing.title,
      venue: listing.venue,
      salePrice: soldConfirm.salePrice,
      buyerShipping: 0,
      actualShipping: 3.0,
      platformFees,
      paymentFees,
      packagingCost: product?.packagingCost ?? 0,
      sourceLotId: product?.sourceLotId ?? "",
      trackingNumber: "",
      shipped: false,
      packed: false,
      buyerName,
      buyerAddress,
      ebayOrderId,
      ebayTransactionId,
      createdAt: new Date().toISOString(),
      suppliesBreakdown:
        suppliesBreakdown.length > 0 ? suppliesBreakdown : undefined,
    };
    // TNK-102 cross-listing: completeSale now also auto-ends other Active
    // listings for the same product. The returned summary drives the toast
    // so the operator knows what closed.
    try {
      const { supersededListings } = await completeSale(
        listing.productId,
        listing.id,
        sale,
        listingCardCount,
      );
      setSoldConfirm(null);
      if (supersededListings.length > 0) {
        const venues = supersededListings.map((s) => s.venue).join(", ");
        setSupersedeToast(
          `Sold on ${listing.venue}; ended ${supersededListings.length} other listing${supersededListings.length === 1 ? "" : "s"} (${venues})`,
        );
        // Auto-dismiss after 6s.
        setTimeout(() => setSupersedeToast(null), 6000);
      }
      void navigate({ to: "/sales" });
    } catch (err) {
      // 409 stale listing — the server rejected the close because the row
      // wasn't Active anymore. Surface it so the operator can refetch.
      setSoldConfirm(null);
      setSupersedeToast(
        err instanceof Error
          ? `Mark Sold failed: ${err.message}`
          : "Mark Sold failed — please retry",
      );
      setTimeout(() => setSupersedeToast(null), 6000);
    }
  }, [
    soldConfirm,
    liveListings,
    products,
    shippingSupplies,
    completeSale,
    navigate,
  ]);

  const openEditSupplies = (listing: LiveListing) => {
    setDraftSupplyIds(listing.supplyIds ?? []);
    setEditSuppliesListingId(listing.id);
  };

  const toggleDraftSupply = (supplyId: string) => {
    setDraftSupplyIds((prev) =>
      prev.includes(supplyId)
        ? prev.filter((id) => id !== supplyId)
        : [...prev, supplyId],
    );
  };

  // Bulk eligibility: End/Extend target active eBay listings; Relist targets
  // ended ones. Computed from the current selection so the action bar can show
  // accurate counts and the runner only touches qualifying targets.
  const selectedListings = useMemo(
    () => liveListings.filter((l) => selectedIds.has(l.id)),
    [liveListings, selectedIds],
  );
  // TNK-102: status='Active' is the authoritative "still open" signal; date
  // expiry only adds to the closed side.
  const isActiveEbay = (l: LiveListing) =>
    l.venue === "eBay" &&
    !!l.ebayListingId &&
    (l.status ?? "Active") === "Active" &&
    !(l.endsAt && new Date(l.endsAt).getTime() <= Date.now());
  const isEndedEbay = (l: LiveListing) =>
    l.venue === "eBay" &&
    !!l.ebayListingId &&
    ((l.status ?? "Active") !== "Active" ||
      (!!l.endsAt && new Date(l.endsAt).getTime() <= Date.now()));
  const eligibleEnd = selectedListings.filter(isActiveEbay);
  const eligibleExtend = selectedListings.filter(isActiveEbay);
  const eligibleRelist = selectedListings.filter(isEndedEbay);

  const runBulk = async (
    action: "end" | "extend" | "relist",
    targets: LiveListing[],
  ) => {
    if (targets.length === 0) return;
    setBulkConfirm(null);
    setBulkRunning(true);
    setBulkProgress({ action, total: targets.length, done: 0, results: [] });
    for (const listing of targets) {
      let ok = false;
      let error: string | undefined;
      try {
        if (action === "end") {
          if (!bulkEndReason) throw new Error("No reason selected");
          if (!listing.ebayListingId) throw new Error("No eBay item id");
          await endEbayListing(listing.ebayListingId, bulkEndReason);
          void endLiveListing(listing.id, {
            reason: bulkEndReason,
            pushExternal: false,
          });
          if (bulkEndReason !== "Incorrect") {
            const product = products.find((p) => p.id === listing.productId);
            if (product) updateProduct({ ...product, status: "Sold" });
          }
        } else if (action === "extend") {
          if (!listing.ebayListingId) throw new Error("No eBay item id");
          const data = await extendEbayListingDuration({
            itemId: listing.ebayListingId,
            duration: bulkDuration,
          });
          const newEndsAt =
            data.endTime ??
            (bulkDuration !== "GTC"
              ? new Date(
                  Date.now() + (bulkDuration as number) * 86_400_000,
                ).toISOString()
              : undefined);
          updateLiveListing({ ...listing, endsAt: newEndsAt });
        } else {
          if (!listing.ebayListingId) throw new Error("No eBay item id");
          const lt = listing.listingType ?? "Auction";
          const body: Parameters<typeof relistEbayListing>[0] = {
            sourceItemId: listing.ebayListingId,
            productId: listing.productId,
            title: listing.title.substring(0, 80),
            listingType: lt,
          };
          if (lt === "Auction") {
            body.startingBid = listing.startingBid ?? listing.askingPrice;
            if (listing.reservePrice != null)
              body.reservePrice = listing.reservePrice;
            if (listing.buyItNowPrice != null)
              body.price = listing.buyItNowPrice;
          } else {
            body.price = listing.askingPrice;
          }
          const result = await relistEbayListing(body);
          const newListing: LiveListing = {
            ...listing,
            id: generateId("LL"),
            startedAt: new Date().toISOString(),
            endsAt: result.endsAt,
            ebayListingId: result.itemId,
            ebayListingUrl: result.listingUrl,
          };
          relist(listing.productId, listing.id, newListing);
        }
        ok = true;
      } catch (err) {
        error = err instanceof Error ? err.message : "Failed";
      }
      const result: BulkProgressResult = {
        listingId: listing.id,
        title: listing.title,
        ok,
        ...(error ? { error } : {}),
      };
      setBulkProgress((prev) =>
        prev
          ? { ...prev, done: prev.done + 1, results: [...prev.results, result] }
          : prev,
      );
    }
    setBulkRunning(false);
  };

  const closeBulkProgress = () => {
    if (bulkRunning) return;
    setBulkProgress(null);
    exitSelectMode();
  };

  const commitEditSupplies = () => {
    if (!editSuppliesListingId) return;
    updateListingSupplies(editSuppliesListingId, draftSupplyIds);
    setEditSuppliesListingId(null);
    setBanner({ type: "success", text: "Supplies updated" });
  };

  const previewNet = useMemo(() => {
    if (!soldConfirm) return 0;
    const listing = liveListings.find((l) => l.id === soldConfirm.listingId);
    const product = products.find((p) => p.id === listing?.productId);
    const { platformFees, paymentFees } = listing
      ? calcAutoFees(soldConfirm.salePrice, listing.venue)
      : { platformFees: 0, paymentFees: 0 };
    return calcNetProfit({
      salePrice: soldConfirm.salePrice,
      buyerShipping: 0,
      actualShipping: 3.0,
      platformFees,
      paymentFees,
      packagingCost: product?.packagingCost ?? 0,
    } as Sale);
  }, [soldConfirm, liveListings, products]);

  return (
    <div className="space-y-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-foreground">
            Live
            {liveListings.length > 0 && !selectMode && (
              <span className="inline-flex items-center gap-1 rounded-full bg-destructive px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-destructive-foreground">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-destructive-foreground" />
                Live
              </span>
            )}
          </h1>
          <p className="text-sm text-muted-foreground">
            {selectMode ? (
              <>{selectedIds.size} selected</>
            ) : (
              <>
                {liveListings.length} listing
                {liveListings.length !== 1 ? "s" : ""}
                {filtered.length !== liveListings.length
                  ? ` · ${filtered.length} shown`
                  : ""}
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!selectMode && (
            <button
              type="button"
              onClick={handleReconcile}
              disabled={reconciling}
              title="Rebuild live listings from your active eBay listings"
              className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-3 py-1.5 text-xs text-foreground hover:bg-card disabled:opacity-60"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${reconciling ? "animate-spin" : ""}`}
              />
              {reconciling ? "Reconciling…" : "Reconcile from eBay"}
            </button>
          )}
          {liveListings.length > 0 && (
            <button
              type="button"
              onClick={selectMode ? exitSelectMode : enterSelectMode}
              className={
                selectMode
                  ? "inline-flex items-center gap-1 rounded-md bg-secondary px-3 py-1.5 text-xs font-semibold text-secondary-foreground"
                  : "inline-flex items-center gap-1 rounded-md border border-border bg-muted px-3 py-1.5 text-xs text-foreground hover:bg-card"
              }
            >
              {selectMode ? (
                <X className="h-3.5 w-3.5" />
              ) : (
                <CheckSquare className="h-3.5 w-3.5" />
              )}
              {selectMode ? "Done" : "Select"}
            </button>
          )}
        </div>
      </header>

      {banner && (
        <div
          className={
            banner.type === "success"
              ? "flex items-center gap-2 rounded-md bg-emerald-500/15 px-3 py-2 text-sm text-emerald-600"
              : "flex items-center gap-2 rounded-md bg-destructive/15 px-3 py-2 text-sm text-destructive"
          }
        >
          {banner.type === "success" ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <AlertTriangle className="h-4 w-4" />
          )}
          <span className="flex-1">{banner.text}</span>
          <button
            type="button"
            onClick={() => setBanner(null)}
            className="text-current hover:opacity-70"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {supersedeToast && (
        <div className="flex items-center gap-2 rounded-md bg-sky-500/15 px-3 py-2 text-sm text-sky-700">
          <CheckCircle className="h-4 w-4" />
          <span className="flex-1">{supersedeToast}</span>
          <button
            type="button"
            onClick={() => setSupersedeToast(null)}
            className="text-current hover:opacity-70"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {liveListings.length === 0 ? (
        <EmptyState
          icon={<Radio className="h-7 w-7" />}
          title="No live listings yet"
          description='Go to Products → tap "Go Live" on a ready product to start tracking auctions and listings here.'
        />
      ) : (
        <>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-2">
              {FILTERS.map((f) => {
                const active = filter === f.value;
                return (
                  <button
                    key={f.value}
                    type="button"
                    onClick={() => setFilter(f.value)}
                    className={
                      active
                        ? "rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground"
                        : "rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground hover:text-foreground"
                    }
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title…"
              className="w-full rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-secondary focus:outline-none sm:w-64"
            />
          </div>

          {filtered.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground">
              No listings match the current filter.
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((item) => (
                <ListingCard
                  key={item.listing.id}
                  item={item}
                  now={now}
                  selectMode={selectMode}
                  selected={selectedIds.has(item.listing.id)}
                  onToggleSelect={() => toggleSelected(item.listing.id)}
                  watcherCount={watcherCounts[item.listing.id]}
                  unreadCount={unreadMessageCounts[item.listing.id]}
                  effectiveListingType={
                    listingTypeCache[item.listing.id] ??
                    item.listing.listingType
                  }
                  isConfirmingDelete={confirmDeleteId === item.listing.id}
                  soldConfirm={
                    soldConfirm?.listingId === item.listing.id
                      ? soldConfirm
                      : null
                  }
                  previewNet={previewNet}
                  onOpenSoldConfirm={() =>
                    setSoldConfirm({
                      listingId: item.listing.id,
                      salePrice: item.listing.askingPrice,
                    })
                  }
                  onCancelSoldConfirm={() => setSoldConfirm(null)}
                  onConfirmSold={handleCompleteSale}
                  onRequestDelete={() => setConfirmDeleteId(item.listing.id)}
                  onCancelDelete={() => setConfirmDeleteId(null)}
                  onConfirmDelete={() => {
                    deleteLiveListing(item.listing.id);
                    setConfirmDeleteId(null);
                  }}
                  onOpenEnd={() => {
                    setEndListingId(item.listing.id);
                    setEndReason(null);
                  }}
                  onOpenEditSupplies={() => openEditSupplies(item.listing)}
                  onOpenEditPrice={() => setEditPriceListing(item.listing)}
                  onOpenEditDetails={() => setEditDetailsListing(item.listing)}
                  onOpenSendOffer={() => setSendOfferListing(item.listing)}
                  onOpenMessages={() => setMessagesListing(item.listing)}
                  onSellSimilar={() => {
                    const l = item.listing;
                    void navigate({
                      to: "/products/$id",
                      params: { id: l.productId },
                      search: {
                        openGoLive: "true",
                        ...(l.ebayListingId
                          ? { sellSimilarFromItemId: l.ebayListingId }
                          : {}),
                        sellSimilarTitle: l.title,
                        sellSimilarPrice: (l.listingType === "Auction"
                          ? (l.startingBid ?? l.askingPrice)
                          : l.askingPrice
                        ).toString(),
                        sellSimilarListingType: l.listingType ?? "Auction",
                        ...(l.reservePrice != null
                          ? {
                              sellSimilarReservePrice:
                                l.reservePrice.toString(),
                            }
                          : {}),
                        ...(l.buyItNowPrice != null
                          ? {
                              sellSimilarBuyItNowPrice:
                                l.buyItNowPrice.toString(),
                            }
                          : {}),
                        initialSupplyIds: (l.supplyIds ?? []).join(","),
                      },
                    });
                  }}
                  onRelist={() => {
                    const l = item.listing;
                    void navigate({
                      to: "/products/$id",
                      params: { id: l.productId },
                      search: {
                        openGoLive: "true",
                        relistFromListingId: l.id,
                        initialSupplyIds: (l.supplyIds ?? []).join(","),
                        relistAskingPrice: l.askingPrice.toString(),
                        relistListingType: l.listingType ?? "Auction",
                      },
                    });
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}

      <EndListingModal
        open={endListingId !== null}
        onClose={() => {
          if (endSubmitting) return;
          setEndListingId(null);
          setEndReason(null);
        }}
        reason={endReason}
        onReasonChange={setEndReason}
        submitting={endSubmitting}
        onSubmit={submitEndListing}
      />

      <EditSuppliesModal
        open={editSuppliesListingId !== null}
        onClose={() => setEditSuppliesListingId(null)}
        supplies={shippingSupplies}
        draftIds={draftSupplyIds}
        cardCount={(() => {
          const l = liveListings.find((x) => x.id === editSuppliesListingId);
          if (!l) return 0;
          const p = products.find((x) => x.id === l.productId);
          return l.cardCount ?? p?.cardIds?.length ?? 0;
        })()}
        onToggle={toggleDraftSupply}
        onSave={commitEditSupplies}
      />

      <EditPriceModal
        listing={editPriceListing}
        onClose={() => setEditPriceListing(null)}
        onSuccess={(text) => setBanner({ type: "success", text })}
      />

      <EditDetailsModal
        listing={editDetailsListing}
        onClose={() => setEditDetailsListing(null)}
        onSuccess={(text) => setBanner({ type: "success", text })}
      />

      <SendOfferModal
        listing={sendOfferListing}
        watcherCount={
          sendOfferListing ? (watcherCounts[sendOfferListing.id] ?? 0) : 0
        }
        onClose={() => setSendOfferListing(null)}
        onSuccess={(text, newWatcherCount) => {
          if (sendOfferListing) {
            setWatcherCounts((prev) => ({
              ...prev,
              [sendOfferListing.id]: newWatcherCount,
            }));
          }
          setBanner({ type: "success", text });
        }}
      />

      <MessagesModal
        listing={messagesListing}
        onClose={() => setMessagesListing(null)}
        onUnreadCountChange={(listingId, unread) =>
          setUnreadMessageCounts((prev) => ({ ...prev, [listingId]: unread }))
        }
        onSuccess={(text) => setBanner({ type: "success", text })}
      />

      <BulkEndModal
        open={bulkConfirm === "end"}
        targets={eligibleEnd}
        reason={bulkEndReason}
        onReasonChange={setBulkEndReason}
        onClose={() => setBulkConfirm(null)}
        onSubmit={() => void runBulk("end", eligibleEnd)}
      />
      <BulkExtendModal
        open={bulkConfirm === "extend"}
        targets={eligibleExtend}
        duration={bulkDuration}
        onDurationChange={setBulkDuration}
        onClose={() => setBulkConfirm(null)}
        onSubmit={() => void runBulk("extend", eligibleExtend)}
      />
      <BulkRelistModal
        open={bulkConfirm === "relist"}
        targets={eligibleRelist}
        onClose={() => setBulkConfirm(null)}
        onSubmit={() => void runBulk("relist", eligibleRelist)}
      />
      {bulkProgress && (
        <BulkProgressModal
          open
          action={bulkProgress.action}
          total={bulkProgress.total}
          done={bulkProgress.done}
          running={bulkRunning}
          results={bulkProgress.results}
          onClose={closeBulkProgress}
        />
      )}

      {selectMode && (
        <div className="sticky bottom-0 -mx-6 mt-4 flex flex-wrap items-center gap-2 border-t border-border bg-card px-6 py-3 shadow-lg">
          <button
            type="button"
            onClick={() => {
              if (eligibleEnd.length === 0) return;
              setBulkEndReason("OtherListingError");
              setBulkConfirm("end");
            }}
            disabled={eligibleEnd.length === 0}
            className="inline-flex items-center gap-1 rounded-md bg-destructive px-3 py-2 text-xs font-semibold text-destructive-foreground hover:opacity-90 disabled:opacity-40"
          >
            <Slash className="h-3.5 w-3.5" />
            End ({eligibleEnd.length})
          </button>
          <button
            type="button"
            onClick={() => {
              if (eligibleExtend.length === 0) return;
              setBulkConfirm("extend");
            }}
            disabled={eligibleExtend.length === 0}
            className="inline-flex items-center gap-1 rounded-md bg-muted px-3 py-2 text-xs font-semibold text-foreground hover:bg-card hover:ring-1 hover:ring-border disabled:opacity-40"
          >
            <Clock className="h-3.5 w-3.5" />
            Extend ({eligibleExtend.length})
          </button>
          <button
            type="button"
            onClick={() => {
              if (eligibleRelist.length === 0) return;
              setBulkConfirm("relist");
            }}
            disabled={eligibleRelist.length === 0}
            className="inline-flex items-center gap-1 rounded-md bg-secondary px-3 py-2 text-xs font-semibold text-secondary-foreground hover:opacity-90 disabled:opacity-40"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Relist ({eligibleRelist.length})
          </button>
          <div className="flex-1" />
          <span className="text-xs text-muted-foreground">
            {selectedIds.size} selected
          </span>
        </div>
      )}
    </div>
  );
}

interface EnrichedItem {
  listing: LiveListing;
  product: ReturnType<typeof useApp>["products"][number] | undefined;
  packagedCards: ReturnType<typeof useApp>["cards"];
  supplies: ReturnType<typeof useApp>["shippingSupplies"];
  suppliesCost: number;
  cardValue: number;
}

interface ListingCardProps {
  item: EnrichedItem;
  now: number;
  selectMode: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  watcherCount: number | undefined;
  unreadCount: number | undefined;
  effectiveListingType: "FixedPrice" | "Auction" | undefined;
  isConfirmingDelete: boolean;
  soldConfirm: { listingId: string; salePrice: number } | null;
  previewNet: number;
  onOpenSoldConfirm: () => void;
  onCancelSoldConfirm: () => void;
  onConfirmSold: () => void;
  onRequestDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  onOpenEnd: () => void;
  onOpenEditSupplies: () => void;
  onOpenEditPrice: () => void;
  onOpenEditDetails: () => void;
  onOpenSendOffer: () => void;
  onOpenMessages: () => void;
  onSellSimilar: () => void;
  onRelist: () => void;
}

// eBay requires fixed-price listings to age ~12h before Send Offer is accepted.
// Hide the button until then so the seller doesn't hit a confusing rejection.
const SEND_OFFER_MIN_AGE_MS = 12 * 60 * 60 * 1000;

function ListingCard({
  item,
  now,
  selectMode,
  selected,
  onToggleSelect,
  watcherCount,
  unreadCount,
  effectiveListingType,
  isConfirmingDelete,
  soldConfirm,
  previewNet,
  onOpenSoldConfirm,
  onCancelSoldConfirm,
  onConfirmSold,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
  onOpenEnd,
  onOpenEditSupplies,
  onOpenEditPrice,
  onOpenEditDetails,
  onOpenSendOffer,
  onOpenMessages,
  onSellSimilar,
  onRelist,
}: ListingCardProps) {
  const { listing, product, supplies, suppliesCost, cardValue } = item;
  const isSoldOpen = soldConfirm !== null;
  // TNK-102: status='Active' is the authoritative "still open" signal — a
  // status-closed listing (Ended | Sold | Cancelled) is "ended" for UI
  // purposes even if its endsAt hasn't passed. The endsAt date check stays
  // for legacy/auction expiry.
  const isClosedStatus = (listing.status ?? "Active") !== "Active";
  const isEnded =
    isClosedStatus ||
    (!!listing.endsAt && new Date(listing.endsAt).getTime() <= now);
  const isLiveEbay =
    listing.venue === "eBay" && !!listing.ebayListingId && !isEnded;
  const canEndOnEbay = isLiveEbay;
  const canEditOnEbay = isLiveEbay;
  const canSellSimilar = listing.venue === "eBay";
  // Delete is local-only — hide it for active eBay listings so the seller
  // can't accidentally remove a live auction from the app while it's still
  // running on eBay (and incurring fulfillment obligations).
  const canDelete = !isLiveEbay;
  const ageMs = listing.startedAt
    ? now - new Date(listing.startedAt).getTime()
    : Infinity;
  const canSendOffer =
    canEditOnEbay &&
    (effectiveListingType === "FixedPrice" ||
      effectiveListingType === undefined) &&
    (watcherCount ?? 0) > 0 &&
    ageMs >= SEND_OFFER_MIN_AGE_MS;
  const bulkEligible = listing.venue === "eBay" && !!listing.ebayListingId;

  if (selectMode) {
    return (
      <button
        type="button"
        onClick={onToggleSelect}
        disabled={!bulkEligible}
        className={
          selected
            ? "w-full rounded-lg border-2 border-secondary bg-card text-left transition hover:border-secondary"
            : bulkEligible
              ? "w-full rounded-lg border border-border bg-card text-left transition hover:border-secondary/60"
              : "w-full rounded-lg border border-border bg-card text-left opacity-50"
        }
      >
        <div className="flex items-start gap-3 px-4 pt-3">
          <span
            className={
              selected
                ? "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded bg-secondary text-secondary-foreground"
                : "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border border-muted-foreground"
            }
          >
            {selected && <Check className="h-3 w-3" />}
          </span>
          <div className="min-w-0 flex-1 space-y-1">
            <h3 className="line-clamp-2 text-sm font-semibold text-foreground">
              {listing.title}
            </h3>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="rounded bg-secondary/20 px-2 py-0.5 font-semibold text-secondary">
                {listing.venue}
              </span>
              <span>
                {listing.cardCount} card{listing.cardCount !== 1 ? "s" : ""}
              </span>
              {!bulkEligible && <span className="italic">· not eligible</span>}
            </div>
          </div>
          <span className="text-sm font-semibold text-foreground">
            ${listing.askingPrice.toFixed(2)}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3 px-4 py-2 text-xs">
          <CountdownBadge endsAt={listing.endsAt} now={now} />
          <span className="text-muted-foreground">
            Listed {new Date(listing.startedAt).toLocaleDateString()}
          </span>
        </div>
      </button>
    );
  }

  return (
    <div
      className={
        isSoldOpen
          ? "overflow-hidden rounded-lg border border-emerald-500 bg-card"
          : "overflow-hidden rounded-lg border border-border bg-card"
      }
    >
      <div className="flex items-start justify-between gap-3 px-4 pt-3">
        <div className="min-w-0 flex-1 space-y-1">
          <h3 className="line-clamp-2 text-sm font-semibold text-foreground">
            {listing.title}
          </h3>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="rounded bg-secondary/20 px-2 py-0.5 font-semibold text-secondary">
              {listing.venue}
            </span>
            <span>
              {listing.cardCount} card{listing.cardCount !== 1 ? "s" : ""}
            </span>
            {listing.listingType && (
              <span>
                {listing.listingType === "Auction" ? "Auction" : "Buy It Now"}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-0.5 text-right">
          <span className="text-base font-semibold text-foreground">
            ${listing.askingPrice.toFixed(2)}
          </span>
          {listing.listingType === "Auction" &&
            (listing.reservePrice != null || listing.buyItNowPrice != null) && (
              <span className="text-[11px] text-muted-foreground">
                {[
                  listing.reservePrice != null
                    ? `res $${listing.reservePrice.toFixed(2)}`
                    : null,
                  listing.buyItNowPrice != null
                    ? `BIN $${listing.buyItNowPrice.toFixed(2)}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            )}
          <span className="text-[11px] text-muted-foreground">
            val ${cardValue.toFixed(2)}
          </span>
          {watcherCount !== undefined && watcherCount > 0 && (
            <span className="inline-flex items-center gap-1 rounded bg-secondary/15 px-1.5 py-0.5 text-[10px] font-semibold text-secondary">
              <Eye className="h-3 w-3" />
              {watcherCount} watching
            </span>
          )}
          {unreadCount !== undefined && unreadCount > 0 && (
            <span className="inline-flex items-center gap-1 rounded bg-destructive/15 px-1.5 py-0.5 text-[10px] font-semibold text-destructive">
              <MessageCircle className="h-3 w-3" />
              {unreadCount} unread
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 px-4 py-2 text-xs">
        <CountdownBadge endsAt={listing.endsAt} now={now} />
        <span className="text-muted-foreground">
          Listed {new Date(listing.startedAt).toLocaleDateString()}
        </span>
      </div>

      <div className="flex items-center gap-2 border-t border-border px-4 py-2 text-xs">
        <Package
          className={
            supplies.length > 0
              ? "h-3.5 w-3.5 text-muted-foreground"
              : "h-3.5 w-3.5 text-destructive"
          }
        />
        {supplies.length > 0 ? (
          <>
            <span className="flex-1 truncate text-muted-foreground">
              {supplies.map((s) => s.name).join(", ")}
            </span>
            <span className="text-muted-foreground">
              ${suppliesCost.toFixed(2)}
            </span>
          </>
        ) : (
          <span className="flex-1 text-destructive">No supplies selected</span>
        )}
        <button
          type="button"
          onClick={onOpenEditSupplies}
          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Edit supplies"
        >
          <Pencil className="h-3 w-3" />
        </button>
      </div>

      {listing.ebayListingUrl && (
        <a
          href={listing.ebayListingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 border-t border-border px-4 py-2 text-xs text-destructive hover:underline"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          <span className="truncate">
            View on eBay
            {listing.ebayListingId ? ` · #${listing.ebayListingId}` : ""}
          </span>
        </a>
      )}

      {product && (
        <Link
          to="/products/$id"
          params={{ id: product.id }}
          className="flex items-center gap-2 border-t border-border px-4 py-2 text-xs text-muted-foreground hover:bg-muted"
        >
          <Box className="h-3.5 w-3.5" />
          <span className="flex-1 truncate">
            {product.name || product.productType} · {product.status}
          </span>
          <span className="text-muted-foreground">›</span>
        </Link>
      )}

      {isSoldOpen ? (
        <div className="space-y-3 border-t border-border bg-emerald-500/10 px-4 py-3">
          <div className="text-sm font-semibold text-foreground">
            Confirm Sale at ${soldConfirm.salePrice.toFixed(2)}?
          </div>
          {supplies.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Package className="h-3 w-3" />
              <span className="flex-1 truncate">
                {supplies.map((s) => s.name).join(", ")}
              </span>
              <span className="font-semibold">${suppliesCost.toFixed(2)}</span>
            </div>
          )}
          <div className="text-xs text-muted-foreground">
            Auto-fees applied · Est. net{" "}
            <span
              className={
                previewNet >= 0
                  ? "font-bold text-emerald-600"
                  : "font-bold text-destructive"
              }
            >
              ${previewNet.toFixed(2)}
            </span>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancelSoldConfirm}
              className="rounded-md bg-muted px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirmSold}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
            >
              <Check className="h-3.5 w-3.5" />
              Confirm Sale
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2 border-t border-border px-4 py-2">
          {isConfirmingDelete ? (
            <>
              <span className="flex-1 text-xs text-muted-foreground">
                Remove listing?
              </span>
              <button
                type="button"
                onClick={onCancelDelete}
                className="rounded-md bg-muted px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                Keep
              </button>
              <button
                type="button"
                onClick={onConfirmDelete}
                className="rounded-md bg-destructive px-3 py-1.5 text-xs font-semibold text-destructive-foreground hover:opacity-90"
              >
                Remove
              </button>
            </>
          ) : (
            <>
              {canDelete && (
                <button
                  type="button"
                  onClick={onRequestDelete}
                  className="rounded-md bg-muted p-1.5 text-muted-foreground hover:text-foreground"
                  aria-label="Remove listing"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
              {canEditOnEbay && (
                <button
                  type="button"
                  onClick={onOpenEditPrice}
                  className="rounded-md bg-muted p-1.5 text-muted-foreground hover:text-foreground"
                  aria-label="Edit price"
                >
                  <DollarSign className="h-3.5 w-3.5" />
                </button>
              )}
              {canEditOnEbay && (
                <button
                  type="button"
                  onClick={onOpenEditDetails}
                  className="rounded-md bg-muted p-1.5 text-muted-foreground hover:text-foreground"
                  aria-label="Edit details"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
              {canEndOnEbay && (
                <button
                  type="button"
                  onClick={onOpenEnd}
                  className="rounded-md bg-muted p-1.5 text-muted-foreground hover:text-foreground"
                  aria-label="End listing early"
                >
                  <Slash className="h-3.5 w-3.5" />
                </button>
              )}
              {canSendOffer && (
                <button
                  type="button"
                  onClick={onOpenSendOffer}
                  className="rounded-md bg-secondary/20 p-1.5 text-secondary hover:bg-secondary/30"
                  aria-label="Send offer to watchers"
                >
                  <Tag className="h-3.5 w-3.5" />
                </button>
              )}
              {listing.venue === "eBay" && !!listing.ebayListingId && (
                <button
                  type="button"
                  onClick={onOpenMessages}
                  className="relative rounded-md bg-muted p-1.5 text-muted-foreground hover:text-foreground"
                  aria-label="Buyer messages"
                >
                  <MessageCircle
                    className={
                      (unreadCount ?? 0) > 0
                        ? "h-3.5 w-3.5 text-destructive"
                        : "h-3.5 w-3.5"
                    }
                  />
                  {(unreadCount ?? 0) > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold text-destructive-foreground">
                      {(unreadCount ?? 0) > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </button>
              )}
              {canSellSimilar && (
                <button
                  type="button"
                  onClick={onSellSimilar}
                  className="rounded-md bg-muted p-1.5 text-muted-foreground hover:text-foreground"
                  aria-label="Sell similar — pre-fill a new listing from this one"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              )}
              <div className="flex-1" />
              {isEnded && product ? (
                <button
                  type="button"
                  onClick={onRelist}
                  className="inline-flex items-center gap-1 rounded-md bg-destructive px-3 py-1.5 text-xs font-semibold text-destructive-foreground hover:opacity-90"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Relist
                </button>
              ) : (
                !isEnded && (
                  <button
                    type="button"
                    onClick={onOpenSoldConfirm}
                    className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                  >
                    <CheckCircle className="h-3.5 w-3.5" />
                    Mark Sold
                  </button>
                )
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function EndListingModal({
  open,
  onClose,
  reason,
  onReasonChange,
  submitting,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  reason: EndReason | null;
  onReasonChange: (r: EndReason) => void;
  submitting: boolean;
  onSubmit: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      ariaLabel="End listing early"
      panelClassName="w-[min(440px,92vw)] max-h-[85vh] flex flex-col"
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold text-foreground">
          End listing early
        </h2>
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="text-muted-foreground hover:text-foreground disabled:opacity-50"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-3 overflow-y-auto p-4">
        <p className="text-xs text-muted-foreground">
          This will end the listing on eBay and remove it from Live. Choose a
          reason:
        </p>
        <div className="space-y-2">
          {END_REASONS.map((opt) => {
            const checked = reason === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onReasonChange(opt.value)}
                disabled={submitting}
                className={
                  checked
                    ? "flex w-full items-start gap-3 rounded-md border border-destructive bg-destructive/10 p-3 text-left disabled:opacity-50"
                    : "flex w-full items-start gap-3 rounded-md border border-border bg-muted p-3 text-left hover:border-muted-foreground disabled:opacity-50"
                }
              >
                <span
                  className={
                    checked
                      ? "mt-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-white"
                      : "mt-0.5 flex h-4 w-4 items-center justify-center rounded-full border border-muted-foreground"
                  }
                >
                  {checked && <Check className="h-3 w-3" />}
                </span>
                <span className="flex-1">
                  <span className="block text-sm font-medium text-foreground">
                    {opt.label}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {opt.description}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="rounded-md bg-muted px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!reason || submitting}
          className="inline-flex items-center gap-1 rounded-md bg-destructive px-3 py-1.5 text-xs font-semibold text-destructive-foreground hover:opacity-90 disabled:opacity-50"
        >
          <Slash className="h-3.5 w-3.5" />
          {submitting ? "Ending…" : "End listing"}
        </button>
      </div>
    </Modal>
  );
}

function EditSuppliesModal({
  open,
  onClose,
  supplies,
  draftIds,
  cardCount,
  onToggle,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  supplies: ReturnType<typeof useApp>["shippingSupplies"];
  draftIds: string[];
  cardCount: number;
  onToggle: (id: string) => void;
  onSave: () => void;
}) {
  const total = draftIds.reduce((sum, sid) => {
    const s = supplies.find((x) => x.id === sid);
    return sum + (s ? supplyCostFor(s, cardCount) : 0);
  }, 0);
  // Picker shows currently-enabled supplies plus anything already on the
  // listing (so a disabled supply that was previously selected can still be
  // deselected).
  const visibleSupplies = supplies.filter(
    (s) => s.enabled || draftIds.includes(s.id),
  );
  return (
    <Modal
      open={open}
      onClose={onClose}
      ariaLabel="Edit supplies"
      panelClassName="w-[min(440px,92vw)] max-h-[85vh] flex flex-col"
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold text-foreground">Edit Supplies</h2>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {visibleSupplies.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No supplies defined. Add supplies in Settings.
          </p>
        ) : (
          <div className="space-y-2">
            {visibleSupplies.map((supply) => {
              const checked = draftIds.includes(supply.id);
              return (
                <button
                  key={supply.id}
                  type="button"
                  onClick={() => onToggle(supply.id)}
                  className={
                    checked
                      ? "flex w-full items-center gap-3 rounded-md border border-secondary bg-secondary/10 p-3 text-left"
                      : "flex w-full items-center gap-3 rounded-md border border-border bg-muted p-3 text-left hover:border-muted-foreground"
                  }
                >
                  <span
                    className={
                      checked
                        ? "flex h-4 w-4 items-center justify-center rounded bg-secondary text-secondary-foreground"
                        : "flex h-4 w-4 items-center justify-center rounded border border-muted-foreground"
                    }
                  >
                    {checked && <Check className="h-3 w-3" />}
                  </span>
                  <span className="flex-1 text-sm font-medium text-foreground">
                    {supply.name}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    ${supplyCostFor(supply, cardCount).toFixed(2)}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      {supplies.length > 0 && (
        <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-3">
          <span className="text-xs text-muted-foreground">
            Total:{" "}
            <span className="font-bold text-foreground">
              ${total.toFixed(2)}
            </span>
          </span>
          <button
            type="button"
            onClick={onSave}
            className="inline-flex items-center gap-1 rounded-md bg-secondary px-3 py-1.5 text-xs font-semibold text-secondary-foreground hover:opacity-90"
          >
            <Check className="h-3.5 w-3.5" />
            Save
          </button>
        </div>
      )}
    </Modal>
  );
}

function formatCountdown(endsAt: string, now: number): string {
  const diff = new Date(endsAt).getTime() - now;
  if (diff <= 0) return "Ended";
  const totalSec = Math.floor(diff / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  if (days > 0) return `${days}d ${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h ${mins}m ${secs}s`;
  return `${mins}m ${secs}s`;
}

function CountdownBadge({ endsAt, now }: { endsAt?: string; now: number }) {
  if (!endsAt) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
        <Clock className="h-3 w-3" />
        No end time
      </span>
    );
  }
  const diff = new Date(endsAt).getTime() - now;
  if (diff <= 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-white px-2 py-0.5 text-[11px] font-semibold text-black">
        <Clock className="h-3 w-3" />
        Ended
      </span>
    );
  }
  if (diff <= RED_THRESHOLD_MS) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-destructive/15 px-2 py-0.5 text-[11px] font-semibold text-destructive">
        <AlertCircle className="h-3 w-3" />
        Ending Soon · {formatCountdown(endsAt, now)}
      </span>
    );
  }
  if (diff <= YELLOW_THRESHOLD_MS) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-500">
        <Clock className="h-3 w-3" />
        {formatCountdown(endsAt, now)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-500">
      <Clock className="h-3 w-3" />
      {formatCountdown(endsAt, now)}
    </span>
  );
}
